import React from "react";
import i18n from "../i18n";
import { reportSignal } from "../lib/reportSignal";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(error, errorInfo);
    reportSignal("react_render", error, { componentStack: errorInfo?.componentStack });   // cf-677 : Remontées
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const projectMatch = typeof window !== "undefined"
        ? window.location.pathname.match(/^\/projects\/([0-9a-fA-F-]{36})/)
        : null;
      const projectId = projectMatch ? projectMatch[1] : null;

      const errMessage = this.state.error?.message || String(this.state.error || "");
      const componentStack = this.state.errorInfo?.componentStack || "";
      const errStack = this.state.error?.stack || "";

      return (
        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-6">
          <div className="text-center space-y-6 max-w-3xl w-full">
            <h1 className="text-2xl font-bold text-white">
              {i18n.t('errorBoundary.title')}
            </h1>
            <p className="text-sm text-gray-500">{i18n.t('errorBoundary.teamNotified')}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              {projectId ? (
                <button
                  onClick={() => { window.location.href = `/projects/${projectId}/production`; }}
                  className="px-6 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors font-semibold shadow-lg shadow-violet-600/30"
                >
                  {i18n.t('errorBoundary.backToProject')}
                </button>
              ) : (
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                >
                  {i18n.t('errorBoundary.reload')}
                </button>
              )}
              {projectId && (
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  {i18n.t('errorBoundary.reload')}
                </button>
              )}
              <button
                onClick={() => { window.location.href = "/"; }}
                className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                {i18n.t('errorBoundary.backToDashboard')}
              </button>
            </div>
            {errMessage && (
              <details className="text-left mt-4 bg-white/5 rounded-lg p-4 border border-white/10">
                <summary className="cursor-pointer text-xs text-gray-400 hover:text-white select-none">
                  {i18n.t('errorBoundary.technicalDetails')}
                </summary>
                <div className="mt-3 space-y-2 text-xs font-mono text-gray-300">
                  <div>
                    <div className="text-violet-400 mb-1">{i18n.t('errorBoundary.message')}</div>
                    <pre className="whitespace-pre-wrap break-words bg-black/40 p-2 rounded">{errMessage}</pre>
                  </div>
                  {errStack && (
                    <div>
                      <div className="text-violet-400 mb-1">{i18n.t('errorBoundary.stack')}</div>
                      <pre className="whitespace-pre-wrap break-words bg-black/40 p-2 rounded max-h-48 overflow-auto">{errStack}</pre>
                    </div>
                  )}
                  {componentStack && (
                    <div>
                      <div className="text-violet-400 mb-1">{i18n.t('errorBoundary.component')}</div>
                      <pre className="whitespace-pre-wrap break-words bg-black/40 p-2 rounded max-h-48 overflow-auto">{componentStack}</pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
